"use client"

import Link from "next/link"
import dynamic from "next/dynamic"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { Logo } from "@/shared/components/logo"
import {
  getBlogArticleSlug,
  withOwnedBlogUtm,
} from "@/features/blog/lib/owned-utm"

const LanguageSelector = dynamic(
  () =>
    import("@/shared/components/i18n/language-selector").then(
      (module) => module.LanguageSelector
    ),
  {
    ssr: false,
    loading: () => <span aria-hidden="true" className="block h-9 w-24" />,
  }
)

const footerSections = {
  topics: {
    titleKey: "topics",
    links: [
      { labelKey: "aiTechnology", href: "/blog" },
      { labelKey: "guidesTutorials", href: "/blog" },
      { labelKey: "safetyPrivacy", href: "/blog" },
      { labelKey: "productUpdates", href: "/blog" },
      { labelKey: "tipsTricks", href: "/blog" },
    ],
  },
  product: {
    titleKey: "product",
    links: [
      { labelKey: "exploreCharacters", href: "/explore" },
      { labelKey: "createCharacter", href: "/create" },
      { labelKey: "aiImageGenerator", href: "/generate/image" },
      { labelKey: "chat", href: "/chat" },
      { labelKey: "pricing", href: "/pricing" },
    ],
  },
  company: {
    titleKey: "company",
    links: [
      { labelKey: "blog", href: "/blog" },
      { labelKey: "affiliateProgram", href: "/affiliate" },
      { labelKey: "termsOfService", href: "/legal/terms-of-service" },
      { labelKey: "privacyPolicy", href: "/legal/privacy-policy" },
    ],
  },
  connect: {
    titleKey: "connect",
    links: [
      { labelKey: "xTwitter", href: "https://x.com/pleasur.ai" },
      { labelKey: "instagram", href: "https://instagram.com/pleasur.ai" },
    ],
  },
} as const

export function BlogFooter() {
  const t = useTranslations("blog.footer")
  const currentYear = new Date().getFullYear()
  const articleSlug = getBlogArticleSlug(usePathname())

  return (
    <footer className="border-t border-[#1e2630] bg-[#0a0e14]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="py-12">
          <div className="mb-8">
            <LocalizedLink href="/" className="inline-flex items-center gap-0.5">
              <Logo size="sm" />
            </LocalizedLink>
            <p className="mt-3 max-w-md text-sm text-[#8b949e]">
              {t("tagline")}
            </p>
            <p data-testid="ai-disclosure-site" className="mt-2 max-w-md text-xs text-[#8b949e]">
              {t("aiDisclosure")} {t("minorsNotice")}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {Object.values(footerSections).map((section) => (
              <div key={section.titleKey}>
                <h3 className="blog-footer-heading">
                  {t(`sections.${section.titleKey}`)}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.labelKey}>
                      {link.href.startsWith("http") ? (
                        <Link
                          href={link.href}
                          className="blog-footer-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t(`links.${link.labelKey}`)}
                        </Link>
                      ) : (
                        <LocalizedLink
                          href={withOwnedBlogUtm(link.href, articleSlug)}
                          className="blog-footer-link"
                        >
                          {t(`links.${link.labelKey}`)}
                        </LocalizedLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-[#1e2630]" />

        <div className="flex flex-col items-center justify-between gap-4 py-6 sm:flex-row">
          <p className="text-xs text-[#8b949e]">
            {t("copyright", { year: currentYear })}
          </p>
          <LanguageSelector triggerClassName="h-9 w-auto text-[#8b949e]" />
          <p className="text-xs text-[#8b949e]">
            {t("madeWithCare")}
          </p>
        </div>
      </div>
    </footer>
  )
}
